import { useMemo } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { boschGray } from '../../../modules/theme/boschColors';
import { useApiData } from './useApiData';
import {
  buildChartPoints,
  ChartSeries,
  seriesColor,
} from './chartData';
import { ApiStatusBar } from './ApiStatusBar';

const useStyles = makeStyles(theme => ({
  chartBox: {
    padding: theme.spacing(1.5, 1, 0.5, 1),
    minHeight: 280,
  },
  empty: {
    padding: theme.spacing(2),
    color: boschGray[40],
    fontStyle: 'italic',
    fontSize: '0.85rem',
  },
}));

export type ApiLineChartProps = {
  title?: string;
  url?: string;
  inlineJson?: string;
  path?: string;
  xKey: string;
  series: ChartSeries[];
  refreshSeconds?: number;
  /** When true, render filled areas under each line. Useful for
   *  "trend" emphasis vs "discrete measurement". */
  filled?: boolean;
  /** Hide dots on individual datapoints. For high-frequency series
   *  (e.g. one point per second) the dots become visual noise. */
  smooth?: boolean;
};

/**
 * Line / area chart for time series and monotonic metrics:
 * deployment frequency, MTTR, latency over time, etc.
 *
 * Uses `<ComposedChart>` (not `<LineChart>`) so the `filled` flag
 * can swap between `<Line>` and `<Area>` per series without
 * remounting a different parent component, keeping the chart's
 * internal state (legend toggles, hover) intact when an admin
 * tweaks the flag.
 */
export const ApiLineChart = ({
  title,
  url,
  inlineJson,
  path,
  xKey,
  series,
  refreshSeconds,
  filled = false,
  smooth = false,
}: ApiLineChartProps) => {
  const classes = useStyles();
  const { data, loading, error, lastUpdatedAt, refetch } = useApiData({
    url,
    inlineJson,
    path,
    refreshSeconds,
  });

  const points = useMemo(
    () =>
      buildChartPoints({
        rows: Array.isArray(data) ? data : [],
        xKey,
        series,
      }),
    [data, xKey, series],
  );

  return (
    <InfoCard title={title ?? 'Line chart'} noPadding>
      {!url && !inlineJson ? (
        <Typography className={classes.empty}>
          Provide a URL or inline JSON to populate this chart.
        </Typography>
      ) : !Array.isArray(data) && data !== undefined ? (
        <Typography className={classes.empty}>
          Path <code>{path || '(root)'}</code> resolved to a {typeof data},
          not an array.
        </Typography>
      ) : points.length === 0 ? (
        <Typography className={classes.empty}>
          No rows to plot. Check the path / API response.
        </Typography>
      ) : (
        <div className={classes.chartBox}>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart
              data={points}
              margin={{ top: 8, right: 16, left: 0, bottom: 16 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={boschGray[90]} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: boschGray[40] }}
                stroke={boschGray[80]}
              />
              <YAxis
                tick={{ fontSize: 11, fill: boschGray[40] }}
                stroke={boschGray[80]}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 12,
                  borderRadius: 4,
                  border: `1px solid ${boschGray[85]}`,
                }}
              />
              {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
              {series.map((s, idx) => {
                const color = seriesColor(s, idx);
                const common = {
                  key: s.dataKey,
                  dataKey: s.dataKey,
                  name: s.label ?? s.dataKey,
                  stroke: color,
                  strokeWidth: 2,
                  /* `monotone` smooths nicely without overshooting
                   * data — `natural` is prettier but invents values
                   * between points that the API never reported. */
                  type: 'monotone' as const,
                  dot: smooth ? false : { r: 2 },
                  activeDot: { r: 4 },
                };
                if (filled) {
                  return (
                    <Area
                      {...common}
                      fill={color}
                      fillOpacity={0.15}
                    />
                  );
                }
                return <Line {...common} />;
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      <ApiStatusBar
        loading={loading}
        error={error}
        lastUpdatedAt={lastUpdatedAt}
        onRefresh={url ? refetch : undefined}
        modeLabel={url ? 'Live API' : 'Inline JSON'}
      />
    </InfoCard>
  );
};
